import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  rotation: number;
  velocityX: number;
  velocityY: number;
}

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#F8B500', '#FF69B4'
];

const generateParticles = (count: number): Particle[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 0,
    y: 0,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: Math.random() * 10 + 5,
    rotation: Math.random() * 360,
    velocityX: (Math.random() - 0.5) * 800,
    velocityY: (Math.random() - 0.5) * 800 - 200,
  }));
};

interface ConfettiExplosionProps {
  onComplete: () => void;
}

export const ConfettiExplosion = ({ onComplete }: ConfettiExplosionProps) => {
  const [particles] = useState(() => generateParticles(60));

  useEffect(() => {
    const timer = setTimeout(onComplete, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {/* CRT Scanline Effect */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0, 0, 0, 0.3) 2px,
            rgba(0, 0, 0, 0.3) 4px
          )`,
        }}
      />

      {/* Confetti Particles */}
      <div className="absolute inset-0 flex items-center justify-center">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{
              x: 0,
              y: 0,
              rotate: 0,
              scale: 1,
              opacity: 1,
            }}
            animate={{
              x: particle.velocityX,
              y: particle.velocityY + 400,
              rotate: particle.rotation + Math.random() * 720,
              scale: [1, 1.2, 0],
              opacity: [1, 1, 0],
            }}
            transition={{
              duration: 2.5,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="absolute"
            style={{
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            }}
          />
        ))}
      </div>

      {/* Achievement Badge */}
      <motion.div
        initial={{ scale: 0, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ 
          type: 'spring', 
          stiffness: 400, 
          damping: 15,
          delay: 0.2 
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 p-1 rounded-2xl shadow-2xl">
          <div className="bg-background/95 backdrop-blur-sm px-8 py-6 rounded-xl text-center">
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: [0, -5, 5, -5, 0] }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="text-4xl mb-2"
            >
              🎮
            </motion.div>
            <h3 className="text-lg font-bold text-foreground mb-1">
              Achievement Unlocked!
            </h3>
            <p className="text-sm text-muted-foreground">
              Retro Gamer
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
